import React, { useState, useEffect } from 'react'
import { useNavigate } from "react-router-dom"
import LogoHpa from './images/logo.png';
import './labo.css'
import { loginUser } from '../utils/auth'

const MgLabo = ({ socket }) => {
  const navigate = useNavigate()
  const [errorMessages, setErrorMessages] = useState({});
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [analyses, setAnalyses] = useState([]);
  
  // Fonction pour mapper les codes d'état aux textes
  const mapLaboStatus = (code) => {
    if (typeof code === 'string') {
      return code;
    }
    const statusMap = {
      0: "En attente",
      1: "En cours",
      2: "Terminé",
      3: "Annulé",
    };
    return statusMap[code] || "En attente";
  };

  // Fonction inverse pour mapper les textes aux codes
  const mapStatusToCode = (statusText) => {
    const statusMap = {
      "En attente": 0,
      "En cours": 1,
      "Terminé": 2,
      "Annulé": 3,
    };
    return statusMap[statusText] || 0;
  };

  // ================================================================================
  
  // Dans MgLabo.js, dans le useEffect
useEffect(() => {
  if (socket) {
    const handleConnect = () => {
      console.log('✅ Laboratoire - Socket connecté');
      socket.emit('module_connected', { module: 'Laboratoire', socketId: socket.id });
    };
    
    socket.on('connect', handleConnect);
    
    if (socket.connected) {
      socket.emit('module_connected', { module: 'Laboratoire', socketId: socket.id });
    }
    
    return () => socket.off('connect', handleConnect);
  }
}, [socket]);

  useEffect(() => {
    if (!socket) return;

    // Écouter les nouveaux Clients avec service laboratoire
    const onNouveauClient = (newData) => {
      console.log("Nouveau Client reçu - NumClient:", newData.numClient, "Données:", newData);
      
      // Vérifier si le service est "laboratoire" avant d'ajouter
      if (newData.service === "laboratoire") {
        setAnalyses((prevAnalyses) => {
          // Vérifie si l'analyse existe déjà via numClient
          const existingIndex = prevAnalyses.findIndex(
            (a) => a.numClient === newData.numClient
          );

          if (existingIndex >= 0) {
            // Mise à jour si le Client existe
            const updatedAnalyses = [...prevAnalyses];
            updatedAnalyses[existingIndex] = {
              ...newData,
              status: mapLaboStatus(newData.isLaboratorized),
            };
            console.log("Client existant mis à jour - NumClient:", newData.numClient);
            return updatedAnalyses;
          } else {
            // Ajout cumulatif si nouveau Client
            const newAnalysis = {
              ...newData,
              status: mapLaboStatus(newData.isLaboratorized),
            };
            console.log("Nouveau Client ajouté - NumClient:", newData.numClient);
            return [...prevAnalyses, newAnalysis];
          }
        });
      }
    };

    // Écouter les mises à jour de statut depuis le serveur
    const onMiseAJourStatut = (updatedClient) => {
      console.log("Mise à jour de statut reçue - NumClient:", updatedClient.numClient, "Nouveau statut:", updatedClient.isLaboratorized);
      
      setAnalyses((prevAnalyses) =>
        prevAnalyses.map((item) =>
          item.numClient === updatedClient.numClient
            ? { 
                ...item, 
                isLaboratorized: updatedClient.isLaboratorized, 
                status: mapLaboStatus(updatedClient.isLaboratorized) 
              }
            : item
        )
      );
    };

    // Configurer les écouteurs d'événements
    socket.on("nouveau_Client", onNouveauClient);
    socket.on("Etat Analyses Mis à Jour", onMiseAJourStatut);

    // Nettoyage à la désactivation du composant
    return () => {
      socket.off("nouveau_Client", onNouveauClient);
      socket.off("Etat Analyses Mis à Jour", onMiseAJourStatut);
    };

  }, [socket]);

// Envoie les mises à jour au serveur
const handleStatusChange = (numClient, newStatusText) => {
  const newCode = mapStatusToCode(newStatusText);
  
  console.log("Changement de statut demandé - NumClient:", numClient, "Nouveau statut:", newStatusText, "Code:", newCode);
  
  // Mise à jour optimiste locale IMMÉDIATE
  setAnalyses((prev) =>
    prev.map((item) =>
      item.numClient === numClient
        ? { 
            ...item, 
            status: newStatusText,
            isLaboratorized: newCode 
          }
        : item
    )
  );

  // Envoyer la mise à jour au serveur pour modifier le fichier JSON
  socket.emit("update_status", {
    numClient: numClient,
    isLaboratorized: newCode
  }, (response) => {
    if (response && response.success) {
      console.log("Statut mis à jour avec succès pour le NumClient:", numClient);
    } else {
      console.error("Erreur lors de la mise à jour pour le NumClient:", numClient, response);
      // Revert local en cas d'erreur
      setAnalyses((prev) =>
        prev.map((item) =>
          item.numClient === numClient
            ? { 
                ...item, 
                status: mapLaboStatus(item.isLaboratorized)
              }
            : item
        )
      );
    }
  });

  if (newStatusText === "Terminé") {
    // Suppression locale après un délai
    setTimeout(() => {
      setAnalyses(prev => prev.filter(item => item.numClient !== numClient));
    }, 2000);
  }
};
  // Fonction pour formater l'affichage des analyses
  const formatAnalyses = (ClientData) => {
    if (!ClientData.analyses || ClientData.analyses.length === 0) {
      return "Aucune analyse spécifiée";
    }
    
    // Si analyses est un tableau d'objets, extraire les noms
    if (Array.isArray(ClientData.analyses)) {
      // Vérifier si le premier élément est un objet avec une propriété 'nom'
      if (ClientData.analyses.length > 0 && ClientData.analyses[0].nom) {
        // C'est un tableau d'objets : extraire les noms
        const nomsAnalyses = ClientData.analyses.map(analyse => analyse.nom);
        return nomsAnalyses.join(', ');
      } else if (typeof ClientData.analyses[0] === 'string') {
        // C'est déjà un tableau de strings
        return ClientData.analyses.join(', ');
      }
    }
    
    // Si analyses est une chaîne de caractères
    if (typeof ClientData.analyses === 'string') {
      return ClientData.analyses;
    }
    
    return "Format d'analyses non reconnu";
  };

  const errors = {
    uname: "Utilisateur Inconnu",
    pass: "Erreur sur Code d'accès"
  };

  const handleLogin = async (event) => {
    event.preventDefault();
    const formData = new FormData(event.target);
    const uname = formData.get('uname');
    const pass = formData.get('pass');

    const result = await loginUser(uname, pass);
    if (result.success) {
      setIsSubmitted(true);
    } else {
      setErrorMessages({ name: "pass", message: result.message || errors.pass });
    }
  };

  const chargerClientsLabo = () => {
    if (!socket || !socket.connected) return;
    socket.emit('load_labo_Clients', (response) => {
      if (response && response.success) {
        const Clients = (response.data || []).map(p => ({
          ...p,
          status: mapLaboStatus(p.isLaboratorized)
        }));
        setAnalyses(Clients);
      }
    });
  };

  useEffect(() => {
    if (isSubmitted && socket) {
      chargerClientsLabo();
    }
  }, [isSubmitted, socket]);

  const renderErrorMessage = (name) =>
    name === errorMessages.name && (
      <div className="message_erreur">{errorMessages.message}</div>
    );

  const renderForm = (
    <div className="form">
      <form className='form_margin BC' onSubmit={handleLogin}>
        <br />
        <br />
        <label className='sous_titre'>Utilisateur </label>
        <input className='utilisateur_interne' minLength={4} type="text" name="uname" required />
        {renderErrorMessage("uname")}

        <label className='sous_titre'>Code d'accès</label>
        <input className='utilisateur_interne' minLength={4} type="password" name="pass" required />
        {renderErrorMessage("pass")}

        <div className='ftt__footer BC'>
          <button className='home__cta'>Connexion</button>
        </div>
      </form>
    </div>
  );

return (
  <>
    <div className="entete TC">
      <div className="marges_logo_5px">
        <img className='logo_clinique marges_logo_5px' src={LogoHpa} alt="LNE" id="logo" />
      </div>
      <div className='titre_entete'>
        <h2 className='titre_entete'>LNE - Ndjamena</h2>
        <h3 className='sous_titre_entete'>Laboratoire d'Analyses Médicale</h3>
        <h2 className='sous_titre_entete'>File des travaux techniques</h2>
      </div>
    </div>

    {!isSubmitted ? renderForm : (
      <div className="labo-container">
        <div className="labo-body">
          <table className="table-labo">
            <thead>
              <tr>
                <th><h2>Code OP</h2></th>
                <th><h2>Analyses</h2></th>
                <th><h2>État Labo</h2></th>
              </tr>
            </thead>
            <tbody>
              {analyses.length === 0 ? (
                <tr>
                  <td colSpan="3" className="aucune-analyse">
                    Aucune analyse en cours pour le laboratoire
                  </td>
                </tr>
              ) : (
                analyses.map((item) => (
                  <tr key={item.numClient}>
                    <td className="tableCodeOP">{item.numClient}</td>
                    <td className="tableAnalyses">
                      <div className="analyses-list">
                        {formatAnalyses(item)}
                      </div>
                    </td>
                    <td>
                      <select className="etatLabo"
                        value={item.status}
                        onChange={(e) =>
                          handleStatusChange(item.numClient, e.target.value)
                        }
                      >
                        {["En attente", "En cours", "Terminé", "Annulé"].map(
                          (option) => (
                            <option key={option} value={option}>
                              {option}
                            </option>
                          )
                        )}
                      </select>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    )}
  </>
);
}
export default MgLabo;
